import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { GuestOnly, RequireRole } from './auth/guards';
import { useAuth } from './auth/AuthProvider';
import { FullLoader, Forbidden, NotFound } from './components/States';
import { ParentShell, StudentShell } from './components/Shells';
import { StudioShell } from './studio/StudioShell';
import { configured } from './lib/supabase';
import Landing from './routes/landing/Landing';

const Register = lazy(() => import('./routes/auth/Register'));
const VerifyEmail = lazy(() => import('./routes/auth/VerifyEmail'));
const AuthCallback = lazy(() => import('./routes/auth/AuthCallback'));
const Login = lazy(() => import('./routes/auth/Login'));
const ResetPassword = lazy(() => import('./routes/auth/ResetPassword'));
const UpdatePassword = lazy(() => import('./routes/auth/UpdatePassword'));
const ChildOnboarding = lazy(() => import('./routes/parent/ChildOnboarding'));
const Children = lazy(() => import('./routes/parent/Children'));
const ChildDetail = lazy(() => import('./routes/parent/ChildDetail'));
const StudentHome = lazy(() => import('./routes/student/Home'));
const StudentPath = lazy(() => import('./routes/student/Path'));
const StudentProgress = lazy(() => import('./routes/student/Progress'));
const Achievements = lazy(() => import('./routes/student/Achievements'));
const LessonPage = lazy(() => import('./routes/student/LessonPage'));
const ReviewPage = lazy(() => import('./routes/student/LessonPage').then((m) => ({ default: m.ReviewPage })));
const Admin = lazy(() => import('./routes/admin/Admin'));
const Start = lazy(() => import('./routes/trial/Start'));
const Trial = lazy(() => import('./routes/trial/Trial'));
const Continue = lazy(() => import('./routes/trial/Continue'));
const Checkout = lazy(() => import('./routes/trial/Checkout'));
const Legal = lazy(() => import('./routes/legal/Legal'));
const StudioHome = lazy(() => import('./studio/StudioHome'));
const StudioEditor = lazy(() => import('./studio/Editor'));
const StudioMedia = lazy(() => import('./studio/Media'));
const StudioStudents = lazy(() => import('./studio/Students'));
const StudioStudentDetail = lazy(() => import('./studio/Students').then((m) => ({ default: m.StudentDetail })));

/** Пробна форма лише для гостей; після входу (зокрема щойно кодом) — /start вирішує, куди далі. */
function TrialGate() {
  const { session, profile, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (session && profile) return <Navigate to="/start?intent=trial" replace />;
  return <Trial />;
}

function NotConfigured() {
  return <Forbidden text="Застосунок не налаштовано: задайте VITE_SUPABASE_URL і VITE_SUPABASE_ANON_KEY (див. README)." />;
}

export default function App() {
  return (
    <Suspense fallback={<FullLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<Legal doc="privacy" />} />
        <Route path="/terms" element={<Legal doc="terms" />} />
        {!configured ? <Route path="*" element={<NotConfigured />} /> : (
          <>
            {/* ТЗ v5.4: вхід з рекламного сайту, пробна реєстрація, точка входу в оплату */}
            <Route path="/start" element={<Start />} />
            <Route path="/trial" element={<TrialGate />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/update-password" element={<UpdatePassword />} />

            <Route path="/onboarding/child" element={<RequireRole role="parent"><ParentShell><ChildOnboarding /></ParentShell></RequireRole>} />
            <Route path="/parent" element={<Navigate to="/parent/children" replace />} />
            <Route path="/parent/children" element={<RequireRole role="parent"><ParentShell><Children /></ParentShell></RequireRole>} />
            <Route path="/parent/child/:id" element={<RequireRole role="parent"><ParentShell><ChildDetail /></ParentShell></RequireRole>} />

            <Route path="/app" element={<RequireRole role="student"><StudentShell><StudentHome /></StudentShell></RequireRole>} />
            <Route path="/app/path" element={<RequireRole role="student"><StudentShell><StudentPath /></StudentShell></RequireRole>} />
            <Route path="/app/progress" element={<RequireRole role="student"><StudentShell><StudentProgress /></StudentShell></RequireRole>} />
            <Route path="/app/achievements" element={<RequireRole role="student"><StudentShell><Achievements /></StudentShell></RequireRole>} />
            <Route path="/app/continue" element={<RequireRole role="student"><StudentShell><Continue /></StudentShell></RequireRole>} />
            <Route path="/app/lesson/:id" element={<RequireRole role="student"><LessonPage /></RequireRole>} />
            <Route path="/app/review/:sessionId" element={<RequireRole role="student"><ReviewPage /></RequireRole>} />

            <Route path="/studio" element={<RequireRole role="editor"><StudioShell><StudioHome /></StudioShell></RequireRole>} />
            <Route path="/studio/lesson/:id" element={<RequireRole role="editor"><StudioShell><StudioEditor /></StudioShell></RequireRole>} />
            <Route path="/studio/media" element={<RequireRole role="editor"><StudioShell><StudioMedia /></StudioShell></RequireRole>} />
            <Route path="/studio/students" element={<RequireRole role="teacher"><StudioShell><StudioStudents /></StudioShell></RequireRole>} />
            <Route path="/studio/students/:id" element={<RequireRole role="teacher"><StudioShell><StudioStudentDetail /></StudioShell></RequireRole>} />
            <Route path="/admin" element={<RequireRole role="admin"><ParentShell><Admin /></ParentShell></RequireRole>} />
            <Route path="*" element={<NotFound />} />
          </>
        )}
      </Routes>
    </Suspense>
  );
}
